import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * NotificationsPopup
 *
 * Bell-icon trigger anchored to the top-right of the slate header that
 * opens a dropdown showing the user's notifications.
 *
 * Data:
 *   - Live notifications via React Query (`listNotifications`, which hits
 *     `GET /v1/notification/list` on the authenticated API surface).
 *   - Read/unread is server-driven. Each `NotificationView` arrives with
 *     a precomputed `isRead` flag derived from `Account.notificationsViewedAt`
 *     (the server-side cursor). Opening the popup fires
 *     `POST /v1/notification/mark-viewed`, which advances the cursor —
 *     the next list fetch then returns every row as read. The badge
 *     resets in the same render via an optimistic cache update so the
 *     UI doesn't wait on the round-trip.
 *
 * Visual structure:
 *   ┌──────────────────────────┐
 *   │ NOTIFICATIONS   3 unread │  ← header
 *   ├──────────────────────────┤
 *   │ UNREAD                   │  ← section title (rendered only when
 *   │ [avatar] notshenita …  6m│    there's an unread bucket)
 *   │ [avatar] notshenita …  7m│
 *   ├──────────────────────────┤
 *   │ EARLIER                  │  ← section title (rendered only when
 *   │ [avatar] alice …       2d│    there's a read bucket AND at least
 *   │ …                        │    one unread row above; otherwise the
 *   └──────────────────────────┘    read rows render without a header)
 */
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { faBell } from "@fortawesome/pro-solid-svg-icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FaIcon } from "@shared/client/components/fa-icon";
import { Popover, PopoverContent, PopoverTrigger, } from "@shared/client/components/ui/popover/popover";
import { markNotificationsViewed } from "@/api/command-api";
import { listNotifications } from "@/api/query-api";
import { toNotificationItemVM } from "./notification-mapping";
import "./notifications-popup.scss";
// Hard cap on what we ask the server for in a single page. The bell only
// ever surfaces "recent" notifications, so we don't paginate — 50 covers
// every reasonable in-session burst without forcing a follow-up fetch.
const POPUP_FETCH_LIMIT = 50;
// Stable query key — single global feed per authenticated user, so a
// flat tuple is enough. If a future surface lists notifications under a
// different scope it should pick its own key prefix.
const NOTIFICATIONS_QUERY_KEY = ["notifications", "list"];
// The full key including the limit suffix — kept as a separate constant
// so the mutation's cache surgery (cancelQueries / getQueryData /
// setQueryData) targets the exact same key the useQuery hook uses. If
// `POPUP_FETCH_LIMIT` ever varies per render we'll need to thread the
// active limit through; today there's only one caller so the constant
// is fine.
const NOTIFICATIONS_LIST_QUERY_KEY = [
    ...NOTIFICATIONS_QUERY_KEY,
    POPUP_FETCH_LIMIT,
];
// Background refresh cadence. Notifications are low-traffic by nature
// (most users get a handful per day) and the popup is opened
// infrequently — every 60 s keeps the bell badge close to live without
// hitting the API on every tab focus.
const REFETCH_INTERVAL_MS = 60_000;
/**
 * 20×20 actor avatar — same visual chrome as the comment-author avatar
 * just a notch smaller (the popup's row density is tighter than the
 * comments rail). Paints `avatarSrc` when the team palette resolved
 * one, else the uppercase `initial` as text inside the warm-tan
 * fallback circle.
 *
 * `aria-hidden` because the row's accessible label (built from the
 * notification's title) already carries the actor's name — the avatar
 * is purely decorative chrome at this density.
 */
const NotificationAvatar = ({ identity }) => {
    return (_jsx("span", { className: "notifications-popup__row-avatar", "aria-hidden": "true", children: identity.avatarSrc !== undefined ? (_jsx("img", { src: identity.avatarSrc, alt: "", className: "notifications-popup__row-avatar-image" })) : (_jsx("span", { className: "notifications-popup__row-avatar-initial", children: identity.initial })) }));
};
const NotificationRow = ({ item, onActivate }) => {
    const isClickable = item.targetPath !== undefined;
    const classes = [
        "notifications-popup__row",
        isClickable ? "notifications-popup__row--clickable" : "",
        item.tone === "warn" ? "notifications-popup__row--warn" : "",
        item.isRead === false ? "notifications-popup__row--unread" : "",
    ]
        .filter(Boolean)
        .join(" ");
    // Body of the row — shared between the button + inert variants so the
    // grid layout stays identical regardless of clickability.
    const body = (_jsxs(_Fragment, { children: [_jsx(NotificationAvatar, { identity: item.avatar }), _jsxs("div", { className: "notifications-popup__row-copy", children: [_jsx("span", { className: "notifications-popup__row-label", children: item.label }), item.sublabel !== undefined && (_jsx("span", { className: "notifications-popup__row-sublabel", children: item.sublabel }))] }), _jsx("span", { className: "notifications-popup__row-time", children: item.timeLabel })] }));
    if (isClickable === false) {
        return _jsx("li", { className: classes, children: body });
    }
    return (_jsx("li", { className: classes, children: _jsx("button", { type: "button", className: "notifications-popup__row-button", onClick: () => onActivate(item), children: body }) }));
};
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const NotificationsPopup = memo(() => {
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { data, isLoading, isError } = useQuery({
        queryKey: NOTIFICATIONS_LIST_QUERY_KEY,
        queryFn: () => listNotifications({ limit: POPUP_FETCH_LIMIT }),
        refetchInterval: REFETCH_INTERVAL_MS,
        // Pull a fresh page when the user tabs back to the viewer — they were
        // likely doing something else when a notification fired.
        refetchOnWindowFocus: true,
    });
    const notifications = useMemo(() => data ?? [], [data]);
    // Map raw API rows to the row VM. Relative-time labels bake in here so
    // the rows themselves stay pure; `isRead` arrives straight from the
    // server's per-row flag.
    const items = useMemo(() => {
        return notifications.map((n) => toNotificationItemVM(n));
    }, [notifications]);
    // Split into unread + earlier buckets. The list is already
    // newest-first, so a stable filter preserves order within each
    // bucket. Computed once per `items` change rather than inline in
    // render so the section headers can read each bucket's length
    // without re-walking the whole list.
    const { unreadItems, readItems } = useMemo(() => {
        const unread = [];
        const read = [];
        for (const item of items) {
            if (item.isRead === false) {
                unread.push(item);
            }
            else {
                read.push(item);
            }
        }
        return { unreadItems: unread, readItems: read };
    }, [items]);
    const unreadCount = unreadItems.length;
    // ---------------------------------------------------------------------------
    // Mark-viewed mutation
    // ---------------------------------------------------------------------------
    //
    // Opening the popup is the moment the user "sees" the current feed. We
    // fire `POST /v1/notification/mark-viewed` to advance the server's
    // cursor, and optimistically rewrite the cached `NotificationView[]`
    // so every row reads as `isRead: true` immediately. The next /list
    // fetch will return the same shape from the server, so onSuccess we
    // simply leave the optimistic state in place; onError we roll back.
    const markViewedMutation = useMutation({
        mutationFn: markNotificationsViewed,
        onMutate: async () => {
            await queryClient.cancelQueries({
                queryKey: NOTIFICATIONS_LIST_QUERY_KEY,
            });
            const previous = queryClient.getQueryData(NOTIFICATIONS_LIST_QUERY_KEY);
            if (previous !== undefined) {
                queryClient.setQueryData(NOTIFICATIONS_LIST_QUERY_KEY, previous.map((n) => ({ ...n, isRead: true })));
            }
            return { previous };
        },
        onError: (_err, _vars, context) => {
            if (context?.previous !== undefined) {
                queryClient.setQueryData(NOTIFICATIONS_LIST_QUERY_KEY, context.previous);
            }
        },
    });
    // `mutate` from `useMutation` is stable across renders, so depending
    // on it in the effect below doesn't cause re-fires. Capturing it via
    // a const makes the dependency intent obvious to readers.
    const markViewedMutate = markViewedMutation.mutate;
    // One mark-viewed call per "open session". Without this latch, the
    // 60 s background refetch (REFETCH_INTERVAL_MS) re-fires the effect
    // every time a new notification lands while the popup is open: the
    // count goes 0 → N, the effect runs, the next /list returns those
    // rows as read, count goes back to 0 — and the user never sees the
    // unread badge or the UNREAD section update. The latch resets on
    // close so the next open re-acknowledges whatever has arrived since.
    // We deliberately don't roll back on close — once a row is
    // acknowledged, it stays acknowledged.
    const hasMarkedViewedThisOpenRef = useRef(false);
    useEffect(() => {
        if (open === false) {
            hasMarkedViewedThisOpenRef.current = false;
            return;
        }
        if (hasMarkedViewedThisOpenRef.current)
            return;
        if (unreadCount === 0)
            return; // nothing to mark; skip the round-trip
        hasMarkedViewedThisOpenRef.current = true;
        markViewedMutate();
    }, [open, unreadCount, markViewedMutate]);
    const handleOpenChange = useCallback((next) => {
        setOpen(next);
    }, []);
    // Row click → navigate to the notification's target surface and close
    // the popup. `targetPath` is always a relative app path so we don't
    // detour through a full page load; `setOpen(false)` happens
    // unconditionally so the popup doesn't linger over the destination
    // even if React Router happens to no-op (clicking a row that points
    // at the page you're already on).
    const handleRowActivate = useCallback((item) => {
        if (item.targetPath !== undefined) {
            navigate(item.targetPath);
        }
        setOpen(false);
    }, [navigate]);
    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------
    // Whether to render the "Earlier" header above the read bucket. Only
    // shown when there's an unread bucket above it — otherwise the read
    // bucket IS the list and a header would be redundant noise.
    const showEarlierHeader = unreadItems.length > 0 && readItems.length > 0;
    return (_jsxs(Popover, { open: open, onOpenChange: handleOpenChange, children: [_jsx(PopoverTrigger, { asChild: true, children: _jsxs("button", { type: "button", className: "notifications-popup__trigger", "aria-label": unreadCount > 0
                        ? `Notifications (${unreadCount} unread)`
                        : "Notifications", "aria-haspopup": "dialog", "aria-expanded": open, children: [_jsx(FaIcon, { icon: faBell, className: "notifications-popup__trigger-icon" }), unreadCount > 0 && (_jsx("span", { className: "notifications-popup__trigger-badge", "aria-hidden": "true", children: unreadCount }))] }) }), _jsxs(PopoverContent, { className: "notifications-popup__panel", side: "bottom", align: "end", sideOffset: 8, children: [_jsxs("div", { className: "notifications-popup__header", children: [_jsx("span", { className: "notifications-popup__header-title", children: "Notifications" }), unreadCount > 0 && (_jsxs("span", { className: "notifications-popup__header-count", children: [unreadCount, " unread"] }))] }), isLoading && items.length === 0 ? (_jsx("div", { className: "notifications-popup__empty", children: "Loading\u2026" })) : isError ? (_jsx("div", { className: "notifications-popup__empty", children: "Couldn't load notifications." })) : items.length === 0 ? (_jsx("div", { className: "notifications-popup__empty", children: "You're all caught up." })) : (_jsxs("div", { className: "notifications-popup__list", children: [unreadItems.length > 0 && (_jsxs("section", { className: "notifications-popup__section", children: [_jsx("h3", { className: "notifications-popup__section-title", children: "Unread" }), _jsx("ul", { className: "notifications-popup__section-list", role: "list", children: unreadItems.map((item) => (_jsx(NotificationRow, { item: item, onActivate: handleRowActivate }, item.id))) })] })), readItems.length > 0 && (_jsxs("section", { className: "notifications-popup__section", children: [showEarlierHeader && (_jsx("h3", { className: "notifications-popup__section-title", children: "Earlier" })), _jsx("ul", { className: "notifications-popup__section-list", role: "list", children: readItems.map((item) => (_jsx(NotificationRow, { item: item, onActivate: handleRowActivate }, item.id))) })] }))] }))] })] }));
});
